import ms from 'ms';

import { CachedRecentSearch, getRecentSearches, postRecentSearch } from '@/redux/api/saveSearch';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '@/redux/store';

import {
    getRecentSearchKeywordFromParams,
    isKeywordInRecentSearches,
    SaveSearchParamsOrKey,
} from './saveSearch.helpers';

const HEADER_RECENT_SEARCHES_COUNT = 5;
const HEADER_RECENT_SEARCH_LOTS_PER_SEARCH = 1;
const RECENT_SEARCHES_CACHE_TIME = ms('5m');

const shouldFetchRecentSearches = (state: GlobalState, force: boolean) => {
    if (force) {
        return true;
    }

    if (state.saveSearch.recentSearchesLoading) {
        return false;
    }

    const recentSearchesLoaded = state.saveSearch.recentSearchesLoaded;

    if (!recentSearchesLoaded) {
        return true;
    }

    return Date.now() - recentSearchesLoaded >= RECENT_SEARCHES_CACHE_TIME;
};

export type SubmitRecentSearchResult = {
    id: unknown;
    key: string;
};

export type SubmitRecentSearchParams = {
    saveSearchParams: SaveSearchParamsOrKey;
    url: string;
};

export const submitRecentSearch = createAsyncThunk<SubmitRecentSearchResult, SubmitRecentSearchParams>(
    'la/domain/saveSearch/submitRecentSearch',
    async ({ saveSearchParams, url }, { dispatch, getState }) => {
        const state = getState();
        const { payload } = await postRecentSearch({
            authToken: getAuthToken(state),
            deployment: getDeployment(state),
            saveSearchParams,
            url,
        });

        await dispatch(fetchRecentSearches({ force: true }));

        return { id: payload, key: url };
    },
    {
        condition: ({ saveSearchParams }, { getState }) => {
            const state = getState();

            if (!isUserLoggedIn(state)) {
                return false;
            }

            const keyword = getRecentSearchKeywordFromParams(saveSearchParams);

            return Boolean(keyword) && !isKeywordInRecentSearches(state, keyword);
        },
    }
);

type FetchRecentSearchesArgs = { force?: boolean };

export const fetchRecentSearches = createAsyncThunk<CachedRecentSearch[], FetchRecentSearchesArgs>(
    'la/domain/saveSearch/fetchRecentSearches',
    async (_, { getState }) => {
        const state = getState();
        const response = await getRecentSearches({
            authToken: getAuthToken(state),
            bidderId: getBidderId(state),
            deployment: getDeployment(state),
            numLotsPerRecentSearch: HEADER_RECENT_SEARCH_LOTS_PER_SEARCH,
            numRecentSearchesToReturn: HEADER_RECENT_SEARCHES_COUNT,
        });

        return response.payload;
    },
    {
        condition: ({ force = false }, { getState }) => {
            const state = getState();

            return isUserLoggedIn(state) && shouldFetchRecentSearches(state, force);
        },
    }
);
